// @flow
import React, { Component } from 'react';
import mapboxgl, { RotationControl } from 'react-mapbox-gl';
import L from 'components/Loadable';
import CircularProgress from 'material-ui/CircularProgress';

import FeatureToggle from 'components/FeatureToggle';
import PropSheet from 'components/PropSheet';
import MissingSubscription from 'components/Map/MissingSubscription';
import WebGLSupportMessage from 'components/Map/WebGLSupportMessage';
import MapContext from 'components/Map/MapContext';

import RTOverlay from 'overlays/RTOverlay';
import ArcSecondGrid from './ArcSecondGrid';

import getMapStyle from 'utils/mapStyle';
import { getSmallestFeature } from 'utils/map';

const MapboxGL = mapboxgl({
  accessToken: process.env.FE_MAPBOX_ACCESS_TOKEN,
  hash: true,
});

const LoadableDrawOverlay = L(
  () => import(/* webpackChunkName: 'drawOverlay' */ 'components/DrawOverlay'),
  { useSpinner: false }
);

function getStyles({ browser, hud, selectedFeature }) {
  const isMobile = browser.lessThan.medium;
  const isSearch = selectedFeature && selectedFeature.type === 's';

  return {
    map: {
      position: 'absolute',
      top: 64,
      left: 0,
      right: 0,
      bottom: 0,
    },
    rotationControl: {
      position: 'fixed',
      top: 'auto',
      bottom: isMobile ? (isSearch ? 160 : 90) : 44,
      right: 26,
      display: isMobile && hud.open ? 'none' : 'flex',
    },
    lngLat: {
      position: 'fixed',
      top: 'auto',
      bottom: isMobile ? (isSearch ? 134 : 66) : 20,
      right: 0,
      display: isMobile && hud.open ? 'none' : 'flex',
      padding: '0 5px',
      backgroundColor: 'rgba(255, 255, 255, 0.5)',
      fontWeight: 'bold',
      color: 'rgba(0,0,0,0.75)',
    },
    progressIndicator: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(255, 255, 255, .65)',
      zIndex: 1302,
    },
    progressIndicatorInner: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginLeft: -40,
      marginTop: -40,
    },
  };
}

export default class Map extends Component<*, *> {
  state = {
    loading: true,
    mapboxGlFailure: false,
    map: null,
    lng: null,
    lat: null
  };

  componentWillMount() {
    this.props.updateVisibleAirspaces();
  }

  componentDidCatch() {
    this.setState({ mapboxGlFailure: true });
  }

  componentDidUpdate(prevProps: Props): * {
    if (prevProps.drawOverlayMode !== this.props.drawOverlayMode) {
      const canvasDOM = this.state.map.getCanvas();
      const isDrawOverLayModeNotSimple =
        this.props.drawOverlayMode === 'draw_polygon' ||
        this.props.drawOverlayMode === 'draw_circle';

      if (
        canvasDOM.style.cursor !== 'crosshair' &&
        this.props.appInDrawMode &&
        isDrawOverLayModeNotSimple
      ) {
        canvasDOM.style.cursor = 'crosshair';
      } else {
        canvasDOM.style.cursor = '';
      }
    }
  }

  onStyleLoad = (map: *) => {
    this.setState({ map });
    const { toggleHUD, toggleMapLayerDrawer, pushRoute } = this.props;

    map.on('dragstart', () => {
      setTimeout(() => {
        // Prevent saved searches from disappearing when panning,
        // as they are not visible otherwise. The user can still
        // dismiss the propsheet by clicking the "x" button.
        const currentPathIsSearch = this.props.router.location.pathname.startsWith(
          '/s/'
        );

        toggleMapLayerDrawer(false);

        if (
          !this.props.appInDrawMode &&
          !currentPathIsSearch &&
          !this.props.followedAircraft
        ) {
          toggleHUD(false);

          if (this.props.router.location.pathname !== '/') {
            pushRoute('/');
          }
        }
      }, 0);
    });

    const canvas = map.getCanvas();

    map.on('mouseenter', 'selection-airspaces-trigger', () => {
      if (!this.props.appInDrawMode) {
        canvas.style.cursor = 'pointer';
      }
    });

    map.on('mouseleave', 'selection-airspaces-trigger', () => {
      if (!this.props.appInDrawMode) {
        canvas.style.cursor = '';
      }
    });

    map.on('click', e => {
      setTimeout(() => {
        toggleMapLayerDrawer(false);

        if (!this.props.appInDrawMode && !this.props.followedAircraft) {
          const feature = getSmallestFeature(
            map,
            e,
            this.props.showAircrafts ? ['aircrafts', 'rt-cluster-circles'] : []
          );

          if (feature && feature.properties.url) {
            pushRoute(`/${feature.properties.url}`);
          } else if (this.props.router.location.pathname !== '/') {
            pushRoute('/');
          }
        }
      }, 0);
    });

    map.on('mousemove', e => {
      if (e.lngLat) {
        this.setState({
          lng: e.lngLat.lng.toFixed(3),
          lat: e.lngLat.lat.toFixed(3)
        })
      }
    });

    map.on('ontouchstart', e => {
      if (e.lngLat) {
        this.setState({
          lng: e.lngLat.lng.toFixed(3),
          lat: e.lngLat.lat.toFixed(3)
        })
      }
    });

    this.setState({ loading: false });
  };

  render() {
    const {
      appInDrawMode,
      viewport,

      visibleAirspaces,
      background,
      styleCategories,
      showAirports,
      showAircrafts,
      showExtrusion,
      selectedFeature,
      hasSubscription,
    } = this.props;

    const { mapboxGlFailure, map } = this.state;

    const styles = getStyles(this.props);
    const mapStyle = getMapStyle({
      visibleAirspaces,
      background,
      styleCategories,
      showAirports,
      showExtrusion,
      selectedFeature,
    });

    if (!hasSubscription) {
      return <MissingSubscription />;
    }

    if (mapboxGlFailure) {
      return <WebGLSupportMessage />;
    }

    return (
      <div>
        <MapboxGL
          containerStyle={styles.map}
          style={mapStyle}
          onStyleLoad={this.onStyleLoad}
          {...viewport}
          fitBoundsOptions={{ maxZoom: 15 }}
        >
          <MapContext.Provider value={map}>
            {showAircrafts && <RTOverlay />}
            <RotationControl style={styles.rotationControl} />
            <div style={styles.lngLat}>
              <span>
                {this.state.lng}, {this.state.lat}
              </span>
            </div>

            {appInDrawMode && <LoadableDrawOverlay />}
            <FeatureToggle flag="hasSubscription">
              <ArcSecondGrid hasSubscription={hasSubscription} />
            </FeatureToggle>
          </MapContext.Provider>
        </MapboxGL>
        {this.state.loading && (
          <CircularProgress
            size={80}
            thickness={5}
            style={styles.progressIndicator}
            innerStyle={styles.progressIndicatorInner}
          />
        )}

        <PropSheet />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./components/Map/Map.js